import type {
  AccountId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import { MongoTeamId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

/**
 * Read the ticket's owning Mongo team id from the in-memory support-ticket
 * collection. Per-team lookups (assignee/customer pickers, related records)
 * should derive the team this way rather than accept a `teamId` prop, keeping
 * the scope colocated with the supportTicketId the component already has.
 *
 * Returns `undefined` while the collection is loading or if the ticket isn't
 * in the local cache; guard on it like a `useQuery` loading state.
 */
export function useSupportTicketTeamId(
  supportTicketId: SupportTicketId,
): string | undefined {
  const collections = useGlobalSupportTicketCollections();
  const result = useLiveQuery(
    (q) =>
      q
        .from({ ticket: collections.supportTicketsList.collection })
        .where(({ ticket }) => eq(ticket.id, supportTicketId)),
    [collections.supportTicketsList.collection, supportTicketId],
  );
  const teamId = result.data?.[0]?.teamId;
  return teamId ? MongoTeamId.toRawId(teamId) : undefined;
}

/**
 * Read the ticket's owning CRDB account id from the in-memory list
 * collection. Same lookup pattern as {@link useSupportTicketTeamId} —
 * used by pickers whose backing data is account-keyed in CRDB (e.g. the
 * tag picker, where tag rows belong to an account_id).
 */
export function useSupportTicketAccountId(
  supportTicketId: SupportTicketId,
): AccountId | undefined {
  const collections = useGlobalSupportTicketCollections();
  const result = useLiveQuery(
    (q) =>
      q
        .from({ ticket: collections.supportTicketsList.collection })
        .where(({ ticket }) => eq(ticket.id, supportTicketId)),
    [collections.supportTicketsList.collection, supportTicketId],
  );
  return result.data?.[0]?.accountId;
}
